import * as styles from "./ComboboxVirtualizedList.css";

import { useImperativeHandle, useLayoutEffect, useRef } from "react";

import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import { useVirtualizer } from "@tanstack/react-virtual";

import type { ComboboxVirtualizedListProps } from "./Combobox.types";
import { ComboboxItem } from "./ComboboxItem";
import { useComboboxScrollElement } from "./ComboboxScroll.context";

export function ComboboxVirtualizedList<T>({
  estimateSize,
  overscan = 20,
  itemKey,
  children,
  description,
  virtualizerRef,
}: ComboboxVirtualizedListProps<T>) {
  const filteredItems = BaseCombobox.useFilteredItems<T>();

  const scrollElement = useComboboxScrollElement();

  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement: () => scrollElement,
    estimateSize,
    overscan,
    paddingStart: 4,
    paddingEnd: 4,
    scrollPaddingEnd: 4,
    scrollPaddingStart: 4,
  });

  useImperativeHandle(virtualizerRef, () => virtualizer);

  const previousItemsRef = useRef(filteredItems);

  useLayoutEffect(() => {
    if (previousItemsRef.current === filteredItems) {
      return;
    }

    previousItemsRef.current = filteredItems;
    virtualizer.scrollToOffset(0);
  }, [filteredItems, virtualizer]);

  if (!filteredItems.length) {
    return null;
  }

  return (
    <div
      role="presentation"
      className={styles.virtualizedPlaceholder}
      style={{ height: virtualizer.getTotalSize() }}
    >
      {virtualizer.getVirtualItems().map((virtualItem) => {
        const item = filteredItems[virtualItem.index];

        if (!item) {
          return null;
        }

        return (
          <ComboboxItem
            key={itemKey(item)}
            ref={virtualizer.measureElement}
            value={item}
            description={description?.(item, virtualItem.index)}
            index={virtualItem.index}
            data-index={virtualItem.index}
            aria-setsize={filteredItems.length}
            aria-posinset={virtualItem.index + 1}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: virtualItem.size,
              transform: `translateY(${virtualItem.start}px)`,
            }}
          >
            {children(item, virtualItem.index)}
          </ComboboxItem>
        );
      })}
    </div>
  );
}
